<template>
  <ul :class="{ 'sub-menu': level > 0, 'sub-menu-1': level === 1, 'sub-menu-2': level === 2 }">
    <li
      v-for="category of getHierarchicalItems(categories, parentId)"
      :key="category.id"
      :class="{ 'is-empty': !category.matches, disabled: !category.matches && !hasSomeSelected(category.id), expanded: expandedCategories.has(category.id) }"
    >
      <span class="menu-item">
        <input
          :id="category.id"
          type="checkbox"
          :checked="isChecked(category.id)"
          @input="emit('input', { key: category.id, value: ($event.target as HTMLInputElement).checked })"
        />
        <label :for="category.id" class="link">
          <span
            class="checkbox"
            :class="{ 'selected-parent': parentIsSelected, 'selected-child': hasSomeSelected(category.id), checked: isChecked(category.id) }"
          />
          {{ category.name }}
          <div v-if="refreshing" class="spinner spinner-border spinner-border-sm refresh-spinner" />
          <template v-else> ({{ category.matches || 0 }})</template>
          <!-- <img class="flag" v-for="country of taxonomyItem.exclusiveToCountries" :key="country" :src="getFlag(country)" /> -->
        </label>
        <button v-if="getHierarchicalItems(categories, category.id)?.length" class="link expand-btn" @click="toggleCategory(category.id)" />
      </span>

      <ToggledMenuComponent
        v-show="expandedCategories.has(category.id)"
        :refreshing="refreshing"
        :categories="categories"
        :parent-id="category.id"
        :parent-is-selected="isChecked(category.id)"
        :level="level + 1"
        @input="emit('input', $event)"
      />
    </li>
  </ul>
</template>

<script lang="ts" setup>
import { SimplifiedCategory } from '@/models/Taxonomy'
import { getHierarchicalItems } from '@/utils/tools'
import { ref } from 'vue'
import { useRoute } from 'vue-router'

interface Props {
  parentId?: string
  categories?: SimplifiedCategory[]
  refreshing?: boolean
  parentIsSelected?: boolean
  level?: number
}

const props = withDefaults(defineProps<Props>(), { level: 0 })

const emit = defineEmits(['input'])
const route = useRoute()

const expandedCategories = ref<Set<string>>(new Set())

const isChecked = (categoryId: string) => {
  const values = route.query._categories
  return values ? String(values).split(',').includes(categoryId) : false
}

const hasSomeSelected = (id: string) =>
  getHierarchicalItems(props.categories, id)?.some(
    (subCategory) => isChecked(subCategory.id) || getHierarchicalItems(props.categories, subCategory.id)?.some((category) => isChecked(category.id)),
  )

const toggleCategory = (categoryId: string) => {
  if (expandedCategories.value.has(categoryId)) {
    expandedCategories.value.delete(categoryId)
  } else {
    expandedCategories.value.add(categoryId)
  }
}
</script>

<style lang="scss" scoped>
ul {
  display: flex;
  flex-wrap: wrap;
  font-size: 0.85rem;
  list-style-type: none;
  padding: 0;

  li {
    width: 100%;
    padding: 0em;
    color: #333;

    &.is-empty * {
      color: #ccc;
      border-color: #ccc;
    }

    a,
    span {
      text-decoration: none;
      color: var(--text-colour);
    }
  }
}

.expanded > .menu-item {
  background-color: #fafafa;
  font-weight: bold;

  border: 1px solid #f0f0f0;
  border-left: none;
  border-right: none;

  border-left: 1px solid var(--danger);

  > .expand-btn::after {
    content: '-';
  }
}

.sub-menu > li > .menu-item {
  border-left: 1px solid #ddd;
  // box-shadow: -2px 0 2px -2px #ddd;
}

.sub-menu-1 {
  margin-left: 1.5em;

  > .expanded > .menu-item {
    border-left: 1px solid var(--selected);
  }
}

.sub-menu-2 {
  margin-left: 1em;
}

.menu-item {
  display: flex;
  width: 100%;
  flex-wrap: nowrap;
  user-select: none;

  :hover {
    background-color: var(--bright-grey);
  }

  .expand-btn {
    outline: none;

    &::after {
      content: '+';
      font-size: 1.2em;
      padding: 0 1.3em;
    }
  }
}

input[type='checkbox'] {
  display: none;
}

label {
  width: 100%;
  margin: 0;
  padding: 0.7em;
}
</style>
